import React, { useState } from 'react';
import { Ticket, ArrowRight, CheckCircle, AlertCircle, Download, BookOpen } from 'lucide-react';
import { t } from './LanguagePicker';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RedeemPage = () => {
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(price);
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!code.trim() || !email.trim()) return;

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/coupons/redeem`, {
        code: code.trim().toUpperCase(),
        email: email.trim(),
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('general_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="redeem-page">
      <header className="redeem-header">
        <a href="/" className="redeem-back" data-testid="redeem-back-link">
          <BookOpen size={24} />
          <span>Atomic Habits for Sharia Bankers</span>
        </a>
      </header>

      <div className="redeem-container">
        {!result ? (
          <div className="redeem-card" data-testid="redeem-form-card">
            <div className="redeem-icon">
              <Ticket size={48} />
            </div>
            <h1 className="redeem-title">Redeem Kupon Diskon</h1>
            <p className="redeem-desc">
              Masukkan kode kupon dan email Anda untuk mendapatkan diskon pembelian buku Atomic Habits for Sharia Bankers dalam format PDF.
            </p>

            <form onSubmit={handleRedeem} className="redeem-form">
              <div className="redeem-input-group">
                <label>Kode Kupon</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="Contoh: AH-XXXXXXXX"
                  required
                  data-testid="redeem-code-input"
                  className="redeem-code-input"
                />
              </div>
              <div className="redeem-input-group">
                <label>Email Anda</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@anda.com"
                  required
                  data-testid="redeem-email-input"
                />
              </div>

              {error && (
                <div className="redeem-error" data-testid="redeem-error">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <button type="submit" className="redeem-btn" disabled={loading} data-testid="redeem-submit-btn">
                {loading ? 'Memproses...' : 'Redeem Kupon'}
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        ) : (
          <div className="redeem-success-card" data-testid="redeem-success">
            <div className="redeem-success-icon">
              <CheckCircle size={56} />
            </div>
            <h1 className="redeem-success-title">{result.message}</h1>

            <div className="redeem-details">
              <div className="redeem-detail-row">
                <span>Tier</span>
                <span className="redeem-tier-badge">{result.coupon.tier}</span>
              </div>
              <div className="redeem-detail-row">
                <span>Diskon</span>
                <span className="redeem-discount">{result.coupon.discount_percent}%</span>
              </div>
              <div className="redeem-detail-row">
                <span>Harga Asli</span>
                <span className="redeem-original">{formatPrice(result.coupon.original_price)}</span>
              </div>
              <div className="redeem-detail-row final">
                <span>Harga Akhir</span>
                <span className="redeem-final">{formatPrice(result.coupon.final_price)}</span>
              </div>
            </div>

            {result.coupon.download_url && (
              <a
                href={result.coupon.download_url}
                target="_blank"
                rel="noopener noreferrer"
                className="redeem-download-btn"
                data-testid="redeem-download-btn"
              >
                <Download size={20} />
                Download Buku (PDF)
              </a>
            )}

            {!result.coupon.download_url && result.coupon.final_price > 0 && (
              <div className="redeem-payment-info">
                <p>Untuk menyelesaikan pembelian dengan harga diskon, silakan hubungi kami:</p>
                <a href="mailto:info@pakdeheru.com" className="redeem-contact-btn">
                  Hubungi info@pakdeheru.com
                </a>
              </div>
            )}

            <div className="redeem-share">
              <p className="redeem-share-label">Bagikan ke rekan bankir syariah Anda:</p>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Saya baru saja mendapatkan diskon ${result.coupon.discount_percent}% untuk buku *Atomic Habits for Sharia Bankers*! Buku transformatif tentang kepemimpinan berkah di dunia perbankan syariah. Daftar sekarang untuk mendapatkan preview gratis: ${window.location.origin}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="redeem-wa-btn"
                data-testid="redeem-whatsapp-share"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Share via WhatsApp
              </a>
            </div>

            <a href="/" className="redeem-home-link">Kembali ke Beranda</a>
          </div>
        )}
      </div>
    </div>
  );
};

export default RedeemPage;
